import { useState } from 'react'
import * as hub from '../hub/roomHub'
import { roomStore } from '../store/room'
import '../css/App.css'

interface Props {
  onClose:    () => void
  onCreated:  (roomCode: string) => void
}

export default function CreateRoomModal({ onClose, onCreated }: Props) {
  const [gameType,   setGameType]   = useState('CATAN')
  const [maxSeats,   setMaxSeats]   = useState(4)
  const [isPrivate,  setIsPrivate]  = useState(false)
  const [password,   setPassword]   = useState('')
  const [busy,       setBusy]       = useState(false)
  const [errMsg,     setErrMsg]     = useState('')
  // SEQ=10112 P8-CK-A — EXT_CK 4 BIT 토글 (EXT_CK만 활성, 나머지 후속 SEQ에서 활성화 — disabled)
  const [extCk,      setExtCk]      = useState(false)
  const [winScore,   setWinScore]   = useState(10)

  async function handleCreate() {
    if (isPrivate && password && password.length < 4) {
      setErrMsg('비밀번호는 4자 이상이어야 합니다.')
      return
    }
    setBusy(true)
    setErrMsg('')

    // RoomCreated 이벤트를 한 번만 수신 (Promise wrapping)
    const result = await new Promise<{ ok: boolean; roomCode?: string; msg?: string }>(resolve => {
      const onCreatedEvt = (data: unknown) => {
        const d = data as { roomCode: string; room: Parameters<typeof roomStore.applyDetail>[0]['room']; members: Parameters<typeof roomStore.applyDetail>[0]['members'] }
        // 서버가 보낸 룸 정보 + 멤버 리스트를 store에 저장 (Room 페이지 즉시 표시용)
        if (d.room && d.members) {
          roomStore.applyDetail({ room: d.room, members: d.members })
        }
        hub.off('RoomCreated', onCreatedEvt as (...a: unknown[]) => void)
        hub.off('Error',       onErrorEvt  as (...a: unknown[]) => void)
        resolve({ ok: true, roomCode: d.roomCode })
      }
      const onErrorEvt = (data: unknown) => {
        const d = data as { action?: string; msg?: string }
        if (d.action !== 'RoomCreate') return
        hub.off('RoomCreated', onCreatedEvt as (...a: unknown[]) => void)
        hub.off('Error',       onErrorEvt  as (...a: unknown[]) => void)
        resolve({ ok: false, msg: d.msg ?? '방 생성 실패' })
      }

      hub.on('RoomCreated', onCreatedEvt as (...a: unknown[]) => void)
      hub.on('Error',       onErrorEvt  as (...a: unknown[]) => void)

      // SEQ=10112 P8-CK-A — 룸 옵션 JSON (EXT_CK 4 BIT + WIN_SCORE_TARGET)
      const optionsJson = JSON.stringify({
        ext_ck:    extCk,
        ext_sea:   false, // 후속 SEQ에서 활성화
        ext_tb:    false,
        ext_5_6:   false,
        win_score: winScore,
      })

      hub.roomCreate(
        gameType,
        optionsJson,
        isPrivate,
        isPrivate && password ? password : null,
        maxSeats,
      ).catch(err => {
        hub.off('RoomCreated', onCreatedEvt as (...a: unknown[]) => void)
        hub.off('Error',       onErrorEvt  as (...a: unknown[]) => void)
        resolve({ ok: false, msg: (err as Error).message ?? '연결 오류' })
      })
    })

    setBusy(false)
    if (result.ok && result.roomCode) {
      onCreated(result.roomCode)
    } else {
      setErrMsg(result.msg ?? '방 생성 실패')
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">방 만들기</h3>

        <label className="modal-label">게임 종류</label>
        <select
          className="modal-select"
          value={gameType}
          onChange={e => {
            const next = e.target.value
            setGameType(next)
            // 게임 종류 변경 시 기본 정원 자동 조정
            if (next === 'TICKET' && (maxSeats < 2 || maxSeats > 5)) setMaxSeats(4)
            if (next === 'CATAN'  && (maxSeats < 3 || maxSeats > 4)) setMaxSeats(4)
            // SEQ=10217 — RUMK: 2~4인. 범위 벗어난 경우 4로 조정
            if (next === 'RUMK'   && (maxSeats < 2 || maxSeats > 4)) setMaxSeats(4)
          }}
          disabled={busy}
        >
          <option value="CATAN">카탄</option>
          <option value="TICKET">티켓 투 라이드</option>
          <option value="RUMK">루미큐브</option>
        </select>

        <label className="modal-label">정원</label>
        <select
          className="modal-select"
          value={maxSeats}
          onChange={e => setMaxSeats(Number(e.target.value))}
          disabled={busy}
        >
          {gameType === 'TICKET'
            ? [2, 3, 4, 5].map(n => (
                <option key={n} value={n}>{n}명</option>
              ))
            // SEQ=10217 — RUMK: 2~4인 (루미큐브 SoT §1 인원 정합)
            : gameType === 'RUMK'
            ? [2, 3, 4].map(n => (
                <option key={n} value={n}>{n}명</option>
              ))
            : [3, 4].map(n => (
                <option key={n} value={n}>{n}명</option>
              ))}
        </select>

        <label className="modal-label">
          <span>비공개</span>
          <input
            type="checkbox"
            className="modal-check"
            checked={isPrivate}
            onChange={e => setIsPrivate(e.target.checked)}
            disabled={busy}
          />
        </label>

        {isPrivate && (
          <>
            <label className="modal-label">비밀번호 (선택)</label>
            <input
              type="password"
              className="modal-input"
              placeholder="4자 이상 (생략 시 코드만으로 입장)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={busy}
            />
          </>
        )}

        {/* catch #17 fix (SEQ=10202 STEP=1, 2026-05-26): 카탄 전용 옵션은 gameType==='CATAN'일 때만 노출.
            TICKET 선택 시 확장팩 fieldset + 승점 목표 숨김 (TTR은 USA 원작 룰만 1차 구현 — 승점 자동 계산, 확장팩은 Phase 8 1910 expansion 별도). */}
        {gameType === 'CATAN' && (
          <>
            {/* SEQ=10112 P8-CK-A — 확장팩 토글 (자유 조합, 본 SEQ는 EXT_CK만 활성) */}
            <fieldset style={{ border: '1px solid #ccc', padding: '8px', margin: '8px 0' }}>
              <legend style={{ fontSize: '12px', color: '#666' }}>확장팩 (자유 조합)</legend>
              <label className="modal-label" style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  className="modal-check"
                  checked={extCk}
                  onChange={e => {
                    setExtCk(e.target.checked)
                    // EXT_CK ON 시 권장 승점 13으로 자동 변경
                    if (e.target.checked && winScore === 10) setWinScore(13)
                    if (!e.target.checked && winScore === 13) setWinScore(10)
                  }}
                  disabled={busy}
                />
                <span> 도시와 기사 (Cities & Knights)</span>
              </label>
              <label className="modal-label" style={{ display: 'block', color: '#999' }}>
                <input type="checkbox" className="modal-check" checked={false} disabled />
                <span> 항해사 (Seafarers) — 후속 SEQ</span>
              </label>
              <label className="modal-label" style={{ display: 'block', color: '#999' }}>
                <input type="checkbox" className="modal-check" checked={false} disabled />
                <span> 무역상과 야만인 (Traders &amp; Barbarians) — 후속 SEQ</span>
              </label>
              <label className="modal-label" style={{ display: 'block', color: '#999' }}>
                <input type="checkbox" className="modal-check" checked={false} disabled />
                <span> 5-6인 합본 (5-6 Player Extension) — 후속 SEQ</span>
              </label>
            </fieldset>

            <label className="modal-label">승점 목표</label>
            <input
              type="number"
              className="modal-input"
              min={7}
              max={20}
              value={winScore}
              onChange={e => setWinScore(Number(e.target.value) || 10)}
              disabled={busy}
            />
          </>
        )}

        {/* SEQ=10217 — RUMK 게임 옵션 안내 (2~4인, 풀룰 MVP 1차 — 추가 옵션 없음) */}
        {gameType === 'RUMK' && (
          <p style={{ fontSize: '12px', color: '#666', margin: '8px 0', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
            루미큐브 풀룰 MVP — 2~4인. 추가 옵션 없음 (라운드 모드·확장은 Phase 1+ 후속).
          </p>
        )}

        {/* TICKET 게임 옵션 안내 (USA 원작 1차 — 추가 옵션 없음) */}
        {gameType === 'TICKET' && (
          <p style={{ fontSize: '12px', color: '#666', margin: '8px 0', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
            티켓 투 라이드 USA 원작 — 추가 옵션 없음 (1910 expansion · long ticket은 Phase 8 후속).
          </p>
        )}

        {errMsg && <p className="modal-err">{errMsg}</p>}

        <div className="modal-btns">
          <button className="btn-cancel" onClick={onClose} disabled={busy}>취소</button>
          <button className="btn-primary" onClick={handleCreate} disabled={busy}>
            {busy ? <span className="spinner" /> : null}생성
          </button>
        </div>
      </div>
    </div>
  )
}
